'use client'

import { useIsClient } from '@/hooks/use-is-client'
import { useAppDispatch, useAppSelector } from '@/lib/store'
import { selectCartDrawerOpen, selectCartItems, selectCartTotal } from '@/lib/store/selectors'
import { closeCartDrawer } from '@/lib/store/features/cart/cartSlice'
import { CartLineItem } from '@/lib/types/operations/cart'
import { computeCartSummary } from '@/lib/utils/price-resolution'
import { RestockingFeeInfo } from './restocking-fee-info'
import { useSession } from 'next-auth/react'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState, useTransition } from 'react'
import { CartItemCollapsed } from './cart-item-collapsed'
import { CartItemExpanded } from './cart-item-expanded'

// Stable identity: a fresh [] each render would retrigger every downstream memo.
const EMPTY_LINE_ITEMS: CartLineItem[] = []

export function CartDrawerV2() {
  const dispatch = useAppDispatch()
  const { data: session } = useSession()
  const priceTier = ((session?.user as any)?.priceTier as 'RETAIL' | 'WHOLESALE') || 'RETAIL'
  // This drawer always renders its DOM (it hides by CSS transform, not by
  // unmounting), and everything inside it comes from the persisted cart, which
  // exists only on the client. Reading the store directly made the client
  // render line items the server never emitted, which is React #418 on every
  // page load with a non-empty cart. Holding the empty-cart shape until the
  // client is live keeps the first client render identical to the server HTML.
  // The drawer is closed on load regardless (see the pathname effect below),
  // so nothing visible flashes.
  const isClient = useIsClient()
  const storeIsOpen = useAppSelector(selectCartDrawerOpen)
  const storeLineItems = useAppSelector(selectCartItems)
  const storeSubtotal = useAppSelector((state) => selectCartTotal(state, priceTier))
  const isOpen = isClient ? storeIsOpen : false
  const lineItems = isClient ? storeLineItems : EMPTY_LINE_ITEMS
  const subtotal = isClient ? storeSubtotal : 0
  const summary = computeCartSummary(subtotal)
  const router = useRouter()
  const pathname = usePathname()

  const [expandedItemId, setExpandedItemId] = useState<string | null>(null)

  // Close the drawer on every route change. Its full-width backdrop would
  // otherwise persist over the next page (e.g. /checkout, /pay) and block that
  // page's controls, because navigating via a header link or the browser back
  // button does not run the drawer's own close handlers. Also guarantees a
  // closed drawer on fresh loads, complementing the REHYDRATE reset in the cart
  // slice.
  useEffect(() => {
    dispatch(closeCartDrawer())
  }, [pathname, dispatch])

  // Reset expanded item when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedItemId(null)
    }
  }, [isOpen])

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    dispatch(closeCartDrawer())
  }, [dispatch])

  const [isNavPending, startNavTransition] = useTransition()

  const handleCheckout = useCallback(() => {
    // Keep the drawer open while /checkout is pending (~2s on staging) so
    // the button can show its in-flight state; the route-change effect above
    // closes the drawer once the navigation commits.
    startNavTransition(() => {
      router.push('/checkout')
    })
  }, [router])

  // Fallback close for pushes that never change pathname (e.g. the drawer
  // opened on /checkout itself): close once the transition settles.
  const wasNavPending = useRef(false)
  useEffect(() => {
    if (wasNavPending.current && !isNavPending) {
      dispatch(closeCartDrawer())
    }
    wasNavPending.current = isNavPending
  }, [isNavPending, dispatch])

  const handleContinueShopping = useCallback(() => {
    router.push('/products')
    handleClose()
  }, [router, handleClose])

  const itemCount = lineItems.length

  // The drawer mounts from the global header on every route. The theme
  // scope sits on <html>, so this wrapper carries no data-theme of its own;
  // it stays as the single root element for the backdrop plus the panel.
  return (
    <div>
      {/* Backdrop */}
      <div
        className={`fixed left-0 right-0 z-[1050] bg-black/50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        style={{ top: 'var(--app-header-h, 64px)', bottom: 'var(--app-bottom-nav-h, 0px)' }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={`fixed right-0 z-[1050] flex w-full max-w-[384px] flex-col bg-background shadow-xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: 'var(--app-header-h, 64px)', bottom: 'var(--app-bottom-nav-h, 0px)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="cw-ui-heading text-lg text-foreground">
            Your Cart
            {itemCount > 0 && (
              <span className="ml-1 text-sm font-normal text-muted">
                ({itemCount})
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted hover:text-foreground transition-colors text-xl leading-none"
            aria-label="Close cart"
          >
            &times;
          </button>
        </div>

        {/* Item list */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {itemCount > 0 ? (
            <div className="space-y-2">
              {lineItems.map((item: CartLineItem) => {
                const isExpanded = expandedItemId === item.id
                return isExpanded ? (
                  <CartItemExpanded
                    key={item.id}
                    item={item}
                    onCollapse={() => setExpandedItemId(null)}
                    priceTier={priceTier}
                  />
                ) : (
                  <CartItemCollapsed
                    key={item.id}
                    item={item}
                    onExpand={() => setExpandedItemId(item.id)}
                    priceTier={priceTier}
                  />
                )
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-muted mb-4">Your cart is empty</p>
              <button
                type="button"
                onClick={handleContinueShopping}
                className="rounded-lg bg-surface px-4 py-2 text-sm font-medium text-foreground hover:bg-overlay transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {itemCount > 0 && (
          <div className="border-t border-border px-4 py-4 space-y-3">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">
                  Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})
                </span>
                <span className="text-foreground tabular-nums">${summary.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Tax ({Math.round(summary.taxRate * 100)}%)</span>
                <span className="text-foreground tabular-nums">${summary.tax.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between pt-1.5 border-t border-border">
                <span className="font-semibold text-foreground">Grand total</span>
                <span className="text-lg font-bold text-foreground tabular-nums">
                  ${summary.grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
            <p className="flex items-center gap-1 text-xs text-muted">
              Cancelled orders are subject to a restocking fee
              <RestockingFeeInfo />
            </p>
            <p className="text-xs text-muted">
              Shipping calculated at checkout
            </p>
            <button
              type="button"
              onClick={handleCheckout}
              disabled={isNavPending}
              className="w-full rounded-lg bg-[var(--btn-primary-bg)] py-3 text-sm font-semibold text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isNavPending ? 'Opening checkout...' : 'Checkout'}
            </button>
            <button
              type="button"
              onClick={handleContinueShopping}
              className="w-full text-center text-sm text-muted hover:text-foreground transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
